import {
  buildPaymentEntryLabel,
} from "./showcase-config-helpers";

export const brandOptions = [
  { value: 1, name: "Karsten" },
  { value: 2, name: "Trussardi" },
  { value: 3, name: "Karsten Decor" },
];

/** Converte data da API (dd/MM/yyyy ou yyyy-MM-dd) para o DateField (DD-MM-YYYY). */
export function mapVitrineDateFromApi(value) {
  if (value == null) return "";
  const trimmed = String(value).trim();
  if (!trimmed) return "";

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    const [day, month, year] = trimmed.split("/");
    return `${day}-${month}-${year}`;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [year, month, day] = trimmed.split("-");
    return `${day}-${month}-${year}`;
  }

  if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
    return trimmed;
  }

  return "";
}

/** Converte data do formulário (DD-MM-YYYY) para a API (yyyy-MM-dd). */
export function mapVitrineDateToApi(value) {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;

  if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
    const [day, month, year] = trimmed.split("-");
    return `${year}-${month}-${day}`;
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    const [day, month, year] = trimmed.split("/");
    return `${year}-${month}-${day}`;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  return null;
}

export function mapBrandFromApi(marca) {
  return brandOptions.find((b) => b.value === marca) ?? null;
}

export function mapPaymentFromDetail(condicoes, paymentOptions) {
  const list = condicoes?.length ? condicoes : [];
  return list
    .map((c) => {
      const found = paymentOptions?.find(
        (p) => p.IdCondicaoPagamento === c.IdCondicaoPagamento,
      );
      const merged = found
        ? {
            ...found,
            PercentualDesconto: c.PercentualDesconto ?? null,
          }
        : {
            ...c,
            Politica: c.Politica ?? "",
            PercentualDesconto: c.PercentualDesconto ?? null,
          };
      return {
        ...merged,
        displayLabel: buildPaymentEntryLabel(merged),
      };
    })
    .filter(Boolean);
}

export function mapPriceTablesFromDetail(codigosOrTabelas, priceTableOptions) {
  const sourceItems = Array.isArray(codigosOrTabelas) ? codigosOrTabelas : [];
  if (!sourceItems.length) return [];

  const normalized = sourceItems.map((item) => {
    if (item != null && typeof item === "object") {
      const cod = item.CodigoTabelaPreco ?? item.CodTabelaPreco;
      return {
        cod: cod != null ? String(cod).trim() : "",
        desc: (item.Descricao ?? item.Nome ?? "").trim(),
      };
    }
    return { cod: String(item).trim(), desc: "" };
  });

  return normalized
    .filter((entry) => entry.cod)
    .map(({ cod, desc }) => {
      const found = priceTableOptions?.find(
        (p) => String(p.CodTabelaPreco) === cod,
      );
      if (found) return found;
      const displayLabel = desc ? `${cod} · ${desc}` : cod;
      return {
        CodTabelaPreco: cod,
        Descricao: desc,
        displayLabel,
      };
    });
}

export function mapSelectedProductFromDetailItem(item) {
  const codigo = item?.IdProduto ?? item?.idProduto;
  // NomeComercial (backend: catálogo/gv.descricao/nmpdtven, sempre WMW) primeiro — mesmo padrão "cru"
  // usado na busca de produtos (ListarProdutosB2b). DescricaoProduto prioriza digital.produto (texto
  // livre, inconsistente por produto — "bonito" pra uns, técnico/cru pra outros) e só entra como
  // último recurso, quando nem o WMW tem nada pra esse produto.
  const descricao =
    item?.NomeComercial ??
    item?.nomeComercial ??
    item?.DescricaoProduto ??
    item?.descricaoProduto ??
    "";
  const foto = item?.Foto ?? item?.foto ?? "";

  return {
    Codigo: codigo,
    Nome: descricao,
    nome: descricao,
    Descricao: descricao,
    descricao,
    FotoPrincipal: foto,
    foto,
    Desconto: item?.DescontoPercentual ?? item?.descontoPercentual ?? 0,
    GradeVenda: item?.GradeVenda ?? item?.gradeVenda ?? "",
    SubgradeVenda: item?.SubgradeVenda ?? item?.subgradeVenda ?? "",
    CorGrade: item?.CorGrade ?? item?.corGrade ?? "",
    TamanhoGrade: item?.TamanhoGrade ?? item?.tamanhoGrade ?? "",
    Marca: item?.Marca ?? item?.marca ?? "",
    LinhaVenda: item?.LinhaVenda ?? item?.linhaVenda ?? "",
    Agrupador: item?.Agrupador ?? item?.agrupador ?? "",
    Colecao: item?.Colecao ?? item?.colecao ?? "",
    Estoque: item?.Estoque ?? item?.estoque ?? null,
  };
}

export function buildShowcasePayload(
  formData,
  selectedProducts,
  representatives = [],
) {
  const paymentConditions = formData.paymentConditions ?? [];
  const idsCondicaoPagamento = paymentConditions
    .map((c) => c.IdCondicaoPagamento)
    .filter(Boolean);

  const condicoesPagamento = paymentConditions
    .filter((c) => c.IdCondicaoPagamento)
    .map((c) => ({
      idCondicaoPagamento: c.IdCondicaoPagamento,
      percentualDesconto: c.PercentualDesconto ?? 0,
    }));

  const codigosTabelaPreco = (formData.priceTables ?? [])
    .map((t) => parseInt(t.CodTabelaPreco, 10))
    .filter((n) => !Number.isNaN(n) && n > 0);

  const firstPayment =
    formData.paymentConditions?.[0] ?? formData.paymentCondition;

  const idsRepresentante = (representatives ?? [])
    .map((r) => r.Id)
    .filter(Boolean);

  return {
    nome: formData.name,
    descricao: formData.description ?? "",
    ativo: true,
    marca: formData.brand?.value,
    dataVigenciaInicio: formData.untilSoldOut
      ? null
      : mapVitrineDateToApi(formData?.initialDate),
    dataVigenciaFim: formData.untilSoldOut
      ? null
      : mapVitrineDateToApi(formData?.finalDate),
    finalizarEstoque: formData.untilSoldOut,
    codigoTabelaPreco:
      codigosTabelaPreco[0] ?? formData.priceTable?.CodTabelaPreco ?? null,
    codigosTabelaPreco,
    descricaoTabelaPreco:
      formData.priceTables?.[0]?.Descricao ??
      formData.priceTable?.Descricao ??
      null,
    idCondicaoPagamento: firstPayment?.IdCondicaoPagamento,
    idsCondicaoPagamento,
    condicoesPagamento,
    prazoMedio: firstPayment?.PrazoMedio
      ? `${firstPayment.PrazoMedio} dias`
      : `${formData.paymentCondition?.PrazoMedio ?? ""} dias`,
    valorMinimo: formData.minimumValue,
    duplicataMinima: formData.minimumInvoice,
    quantidadeMinimaPedido:
      formData.minimumQuantityPerOrder === ""
        ? null
        : formData.minimumQuantityPerOrder,
    porLinha: formData.byLine,
    porPedido: formData.byOrder,
    itensProduto: selectedProducts
      .filter((product) => product?.Codigo != null)
      .map((product) => ({
        idProduto: String(product.Codigo),
        descontoPercentual: product?.Desconto || 0,
      })),
    idsRepresentante,
  };
}
